import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import { exportCsv } from '../../utils/csvExport';
import { getAiVisCssVars } from './aivisTheme';
import { useAuth } from '../../context/AuthContext';
import { isCampaignViewer } from './aivisRoles';
import NoClientState from './NoClientState';
import './ai-vis.css';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ready_for_review', label: 'Ready for Review' },
  { value: 'revisions', label: 'Revisions' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
];

const CLIENT_STATUS_FILTERS = STATUS_FILTERS.map((opt) => (
  opt.value === 'ready_for_review' ? { ...opt, label: 'Approval Needed' } : opt
));

function StatCard({ label, value, accent, subline, sublineColor, progress, loading }) {
  return (
    <div style={{ padding: '18px 20px 16px', borderRadius: 12, border: '1px solid var(--aivis-border)', background: 'var(--aivis-panel)', minHeight: 96 }}>
      <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: accent || 'var(--aivis-text)' }}>
        {loading && (value === '—' || value == null) ? <span style={{ fontSize: 16, color: 'var(--aivis-text-muted)' }}>…</span> : value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--aivis-text-muted)', marginTop: 6 }}>{label}</div>
      {subline ? (
        <div style={{ fontSize: 11, fontWeight: 500, color: sublineColor || 'var(--aivis-text-muted)', marginTop: 4 }}>{subline}</div>
      ) : null}
      {progress != null ? (
        <div style={{ marginTop: 10, height: 6, borderRadius: 999, background: 'var(--aivis-border)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, borderRadius: 999, background: 'var(--aivis-accent)', transition: 'width 0.3s' }} />
        </div>
      ) : null}
    </div>
  );
}

function hasGoogleDocUrl(topic) {
  return Boolean(topic?.google_doc_url && String(topic.google_doc_url).trim());
}

function topicDisplayMeta(topic, { viewer } = {}) {
  const raw = topic.status;
  const hasDoc = hasGoogleDocUrl(topic);
  if (raw === 'Post Implemented') return { label: 'Published', group: 'published' };
  if (raw === 'Ready for Implementation') return { label: 'Approved', group: 'approved' };
  if (raw === 'Post in Revisions') return { label: 'Revisions', group: 'revisions' };
  if (raw === 'Post Sent to Client' || raw === 'Pending Approval Client') {
    return { label: viewer ? 'Approval Needed' : 'Ready for Review', group: 'ready_for_review' };
  }
  if (raw === 'Post Created' || (raw === 'Topic Approved' && (topic.polling_id || hasDoc))) {
    return { label: 'In Progress', group: 'in_progress' };
  }
  return { label: 'Scheduled', group: 'scheduled' };
}

function StatusPill({ status, statusGroup }) {
  return (
    <span className={`aivis-status-capsule aivis-cs-status--${statusGroup}`}>
      {status}
    </span>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
      <path d="M19 15l.7 1.8L21.5 17.5l-1.8.7L19 20l-.7-1.8L16.5 17.5l1.8-.7Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ProcessIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

const PRIMARY_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 12px',
  borderRadius: 999,
  border: 'none',
  background: 'var(--aivis-accent)',
  color: '#fff',
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const SECONDARY_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid var(--aivis-border)',
  background: 'var(--aivis-panel)',
  color: 'var(--aivis-text)',
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
};

const REVISION_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid rgba(212, 72, 63, 0.35)',
  background: 'var(--aivis-danger-soft)',
  color: 'var(--aivis-danger-ink)',
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function formatScheduledDate(raw) {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  const isoDate = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return `${isoDate[2]}/${isoDate[3]}/${isoDate[1].slice(-2)}`;
  }
  const usDate = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (usDate) {
    const mm = usDate[1].padStart(2, '0');
    const dd = usDate[2].padStart(2, '0');
    return `${mm}/${dd}/${usDate[3].slice(-2)}`;
  }
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${String(d.getFullYear()).slice(-2)}`;
}

function ProcessingSpinner() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--aivis-text-muted)' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'et-spin 1s linear infinite' }} aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      Processing…
    </span>
  );
}

function ViewDocButton({ url }) {
  if (!url || !String(url).trim()) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      style={SECONDARY_BTN_STYLE}
      onClick={(e) => e.stopPropagation()}
    >
      <DocIcon />
      View Doc
    </a>
  );
}

function ActionRow({ children, nowrap = false }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: nowrap ? 'nowrap' : 'wrap',
        justifyContent: 'center',
        whiteSpace: nowrap ? 'nowrap' : undefined,
      }}
    >
      {children}
    </div>
  );
}

function ScheduledLabel({ scheduledDate }) {
  const formatted = formatScheduledDate(scheduledDate);
  if (!formatted) return null;
  return (
    <span style={{ fontSize: 12, color: 'var(--aivis-text-muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>
      Scheduled {formatted}
    </span>
  );
}

function CompanyTopicActions({ topic, onGenerate, onApprove, onImplement, onProcessRevision, implementTopicId }) {
  const hasDoc = hasGoogleDocUrl(topic);
  const viewDoc = <ViewDocButton url={topic.google_doc_url} />;
  const isProcessing = !hasDoc && (
    topic.status === 'Post Created'
    || (topic.status === 'Topic Approved' && topic.polling_id)
  );
  const isGeneratedPendingPm = hasDoc && (topic.status === 'Post Created' || topic.status === 'Topic Approved');
  const canGenerate = topic.status === 'Topic Approved' && !topic.polling_id && !hasDoc;

  if (isProcessing) return <ProcessingSpinner />;

  if (isGeneratedPendingPm) {
    return (
      <ActionRow>
        {viewDoc}
        <button type="button" style={PRIMARY_BTN_STYLE} onClick={() => onApprove(topic)}>
          <CheckIcon />
          Approve + Send to client
        </button>
      </ActionRow>
    );
  }

  if (topic.status === 'Ready for Implementation') {
    return (
      <ActionRow>
        {viewDoc}
        <button
          type="button"
          style={PRIMARY_BTN_STYLE}
          disabled={implementTopicId === topic.id}
          onClick={() => onImplement(topic)}
        >
          <CheckIcon />
          {implementTopicId === topic.id ? 'Updating…' : 'Mark Implemented'}
        </button>
      </ActionRow>
    );
  }

  if (topic.status === 'Post Sent to Client') {
    return (
      <ActionRow>
        {viewDoc}
        <span style={{ fontSize: 13, color: 'var(--aivis-text-muted)', fontWeight: 500 }}>Waiting</span>
      </ActionRow>
    );
  }

  if (topic.status === 'Post in Revisions') {
    return (
      <ActionRow>
        {viewDoc}
        <button type="button" style={PRIMARY_BTN_STYLE} onClick={() => onProcessRevision(topic)}>
          <ProcessIcon />
          Process Revision
        </button>
      </ActionRow>
    );
  }

  if (topic.status === 'Post Implemented') {
    return (
      <ActionRow>
        {viewDoc}
        <span style={{ fontSize: 13, color: 'var(--aivis-positive)', fontWeight: 600 }}>Implemented</span>
      </ActionRow>
    );
  }

  if (canGenerate) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
        <button type="button" style={PRIMARY_BTN_STYLE} onClick={() => onGenerate(topic)}>
          <SparklesIcon />
          Generate now
        </button>
        <ScheduledLabel scheduledDate={topic.scheduled_date} />
      </div>
    );
  }

  if (!hasDoc) {
    return formatScheduledDate(topic.scheduled_date)
      ? <ScheduledLabel scheduledDate={topic.scheduled_date} />
      : '—';
  }
  return hasDoc ? viewDoc : '—';
}

function SearchIcon() {
  return (
    <span className="aivis-prompts-search-icon" aria-hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function GenerateModal({ topic, campaignId, onClose, onSuccess }) {
  const [wordCount, setWordCount] = useState('2000');
  const [country, setCountry] = useState('United States');
  const [useClientImages, setUseClientImages] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  async function handleGenerate() {
    setSubmitting(true);
    setModalError('');
    try {
      await api.post(`/aivis/campaigns/${campaignId}/content-strategy/generate/`, {
        topic_id: topic.id,
        article_title: topic.article_title,
        content_format: topic.content_format,
        word_count: wordCount,
        country,
        use_client_images: useClientImages,
      });
      onSuccess();
    } catch (e) {
      setModalError(e.response?.data?.detail || 'Failed to fire generation webhook.');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...getAiVisCssVars(),
          background: 'var(--aivis-panel)', borderRadius: 12, padding: 24, width: 'min(440px, 90vw)',
          border: '1px solid var(--aivis-border)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px', color: 'var(--aivis-text)' }}>
          Generate Blog Post
        </h2>
        <p style={{ fontSize: 13, color: 'var(--aivis-text-muted)', margin: '0 0 20px' }}>
          {topic.article_title}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--aivis-text)' }}>
            Word Count
            <input
              type="text"
              value={wordCount}
              onChange={(e) => setWordCount(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 8, border: '1px solid var(--aivis-border)',
                fontSize: 14, background: 'var(--aivis-bg)', color: 'var(--aivis-text)',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--aivis-text)' }}>
            Country
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 8, border: '1px solid var(--aivis-border)',
                fontSize: 14, background: 'var(--aivis-bg)', color: 'var(--aivis-text)',
              }}
            />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--aivis-text)' }}>
            <button
              type="button"
              role="switch"
              aria-checked={useClientImages}
              onClick={() => setUseClientImages((v) => !v)}
              style={{
                width: 38, height: 22, borderRadius: 999, border: 'none',
                background: useClientImages ? 'var(--aivis-accent)' : 'var(--aivis-border-strong)',
                position: 'relative', cursor: 'pointer', flexShrink: 0,
                transition: 'background 0.15s',
              }}
            >
              <span
                style={{
                  position: 'absolute', top: 3, left: useClientImages ? 19 : 3,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.15s',
                }}
              />
            </button>
            Use Client Images
          </div>
        </div>

        {modalError ? (
          <div className="aivis-auth-error" style={{ marginTop: 14 }}>{modalError}</div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button type="button" className="aivis-csv-export-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" style={PRIMARY_BTN_STYLE} onClick={handleGenerate} disabled={submitting}>
            {submitting ? 'Generating…' : 'Generate now'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ApproveModal({ topic, campaignId, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  const isClientApproval = topic.status === 'Post Sent to Client';
  const endpoint = isClientApproval ? 'approve-client' : 'approve-pm';

  async function handleApprove() {
    setSubmitting(true);
    setModalError('');
    try {
      const body = {
        topic_id: topic.id,
        article_title: topic.article_title,
      };
      if (isClientApproval) {
        body.revision_requested = false;
        body.revision_needed = '';
        body.approved_after_changes = false;
      }
      await api.post(`/aivis/campaigns/${campaignId}/content-strategy/${endpoint}/`, body);
      onSuccess();
    } catch (e) {
      setModalError(e.response?.data?.detail || 'Failed to fire approval webhook.');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...getAiVisCssVars(),
          background: 'var(--aivis-panel)', borderRadius: 12, padding: 24, width: 'min(440px, 90vw)',
          border: '1px solid var(--aivis-border)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px', color: 'var(--aivis-text)' }}>
          {isClientApproval ? 'Approve this draft?' : 'Approve + Send to Client'}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--aivis-text-muted)', margin: '0 0 20px' }}>
          {isClientApproval
            ? `We'll schedule the ${topic.article_title} article for publishing. If something comes up later, please let us know and we can make any necessary updates before it goes live.`
            : `Approve "${topic.article_title}"? This will notify the client to review this blog post.`}
        </p>

        {modalError ? (
          <div className="aivis-auth-error" style={{ marginTop: 0, marginBottom: 14 }}>{modalError}</div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button type="button" className="aivis-csv-export-btn" onClick={onClose} disabled={submitting}>
            {isClientApproval ? 'Keep reviewing' : 'Cancel'}
          </button>
          <button type="button" style={PRIMARY_BTN_STYLE} onClick={handleApprove} disabled={submitting}>
            {submitting ? 'Approving…' : isClientApproval ? 'Approve' : 'Approve + Send to client'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ProcessRevisionModal({ topic, campaignId, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  async function handleSubmit() {
    setSubmitting(true);
    setModalError('');
    try {
      await api.post(`/aivis/campaigns/${campaignId}/content-strategy/process-revision/`, {
        topic_id: topic.id,
        article_title: topic.article_title,
        google_doc_url: topic.google_doc_url || '',
      });
      onSuccess();
    } catch (e) {
      setModalError(e.response?.data?.detail || 'Failed to process revision.');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...getAiVisCssVars(),
          background: 'var(--aivis-panel)', borderRadius: 12, padding: 24, width: 'min(480px, 90vw)',
          border: '1px solid var(--aivis-border)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: 'var(--aivis-text)' }}>
          Process this revision?
        </h2>
        <p style={{ fontSize: 14, color: 'var(--aivis-text)', margin: '0 0 10px', lineHeight: 1.5 }}>
          You are acknowledging that you are processing the revision for <strong>{topic.article_title}</strong>.
        </p>
        <p style={{ fontSize: 13, color: 'var(--aivis-text-muted)', margin: 0, lineHeight: 1.5 }}>
          It will be sent to the client for final approval, or auto-approved, according to the client&apos;s preference for this revision.
        </p>

        {modalError ? (
          <div className="aivis-auth-error" style={{ marginTop: 14, marginBottom: 0 }}>{modalError}</div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button type="button" className="aivis-csv-export-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" style={PRIMARY_BTN_STYLE} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RequestChangesModal({ topic, campaignId, onClose, onSuccess }) {
  const [notes, setNotes] = useState('');
  const [approvedAfterChanges, setApprovedAfterChanges] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const draftUrl = hasGoogleDocUrl(topic) ? topic.google_doc_url : '';

  async function handleSubmit() {
    setSubmitting(true);
    setModalError('');
    try {
      await api.post(`/aivis/campaigns/${campaignId}/content-strategy/approve-client/`, {
        topic_id: topic.id,
        article_title: topic.article_title,
        revision_requested: true,
        revision_needed: notes.trim(),
        approved_after_changes: approvedAfterChanges,
      });
      onSuccess();
    } catch (e) {
      setModalError(e.response?.data?.detail || 'Failed to send revision request.');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...getAiVisCssVars(),
          background: 'var(--aivis-panel)', borderRadius: 12, padding: 24, width: 'min(520px, 90vw)',
          border: '1px solid var(--aivis-border)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 12px', color: 'var(--aivis-text)' }}>
          Send this back to our team?
        </h2>
        <div
          style={{
            margin: '0 0 16px',
            padding: '12px 14px',
            borderRadius: 10,
            background: 'var(--aivis-chip)',
            border: '1px solid rgba(104, 138, 35, 0.28)',
          }}
        >
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--aivis-text)', margin: '0 0 6px', lineHeight: 1.4 }}>
            Add your comments in the Google Doc first.
          </p>
          <p style={{ fontSize: 13, color: 'var(--aivis-text)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Our content team needs notes directly on the draft to make the right changes. Please don't only use the box below.
          </p>
          {draftUrl ? (
            <a
              href={draftUrl}
              target="_blank"
              rel="noreferrer"
              style={{ ...PRIMARY_BTN_STYLE, textDecoration: 'none', width: 'fit-content' }}
            >
              <DocIcon />
              Open the draft
            </a>
          ) : (
            <span style={{ fontSize: 13, color: 'var(--aivis-text-muted)' }}>Draft link isn't available yet.</span>
          )}
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--aivis-text)' }}>
          Anything else you want to flag for us? (optional)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder='e.g. "The intro is too technical for our audience."'
            style={{
              padding: '8px 12px', borderRadius: 8, border: '1px solid var(--aivis-border)',
              fontSize: 14, background: 'var(--aivis-bg)', color: 'var(--aivis-text)',
              resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45,
            }}
          />
        </label>

        {modalError ? (
          <div className="aivis-auth-error" style={{ marginTop: 14 }}>{modalError}</div>
        ) : null}

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 18,
            fontSize: 13,
            color: 'var(--aivis-text)',
            lineHeight: 1.4,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={approvedAfterChanges}
            onChange={(e) => setApprovedAfterChanges(e.target.checked)}
            style={{ marginTop: 2, accentColor: 'var(--aivis-accent)' }}
          />
          Approved for publishing once these changes are made (I don't need to review the article again)
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button type="button" className="aivis-csv-export-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" style={PRIMARY_BTN_STYLE} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send back'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function ContentStrategyPage() {
  const { user } = useAuth();
  const viewer = isCampaignViewer(user);
  const { selectedCampaignId, campaigns } = useOutletContext();
  const selected = campaigns.find((c) => String(c.id) === String(selectedCampaignId));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const searchTimeoutRef = useRef(null);

  const [generateTopic, setGenerateTopic] = useState(null);
  const [approveTopic, setApproveTopic] = useState(null);
  const [processRevisionTopic, setProcessRevisionTopic] = useState(null);
  const [requestChangesTopic, setRequestChangesTopic] = useState(null);
  const [implementTopicId, setImplementTopicId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  useEffect(() => {
    setInputValue('');
    setSearch('');
    setStatusFilter('all');
    setData(null);
    setError('');
    setActionError('');
    setActionSuccess('');
    setGenerateTopic(null);
    setApproveTopic(null);
    setProcessRevisionTopic(null);
    setRequestChangesTopic(null);
  }, [selectedCampaignId]);

  const fetchData = useCallback(() => {
    if (!selectedCampaignId) return;
    api
      .get(`/aivis/campaigns/${selectedCampaignId}/content-strategy/`)
      .then((res) => setData(res.data))
      .catch(() => {});
  }, [selectedCampaignId]);

  useEffect(() => {
    if (!selectedCampaignId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    api
      .get(`/aivis/campaigns/${selectedCampaignId}/content-strategy/`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || 'Could not load content strategy.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCampaignId]);

  // Auto-refresh every 5s to pick up Polling ID / Google Doc URL / status changes from n8n
  useEffect(() => {
    if (!selectedCampaignId) return undefined;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [selectedCampaignId, fetchData]);

  function handleSearchChange(e) {
    const val = e.target.value;
    setInputValue(val);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setSearch(val), 150);
  }

  async function handleImplement(topic) {
    setImplementTopicId(topic.id);
    setActionError('');
    setActionSuccess('');
    try {
      await api.post(`/aivis/campaigns/${selectedCampaignId}/content-strategy/implement/`, {
        topic_id: topic.id,
      });
      setActionSuccess(`"${topic.article_title}" marked as implemented.`);
      setTimeout(() => setActionSuccess(''), 4000);
      fetchData();
    } catch (e) {
      setActionError(e.response?.data?.detail || 'Failed to update status.');
    } finally {
      setImplementTopicId(null);
    }
  }


  const topics = useMemo(() => data?.topics || [], [data]);
  const summary = data?.summary || {};

  const counts = useMemo(() => {
    const c = {
      all: topics.length,
      scheduled: 0,
      in_progress: 0,
      ready_for_review: 0,
      revisions: 0,
      approved: 0,
      published: 0,
    };
    topics.forEach((t) => {
      const group = topicDisplayMeta(t).group;
      if (c[group] != null) c[group] += 1;
    });
    return c;
  }, [topics]);

  const filteredTopics = useMemo(() => {
    let arr = topics;
    if (statusFilter !== 'all') {
      arr = arr.filter((t) => topicDisplayMeta(t).group === statusFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((t) => t.article_title.toLowerCase().includes(q));
    }
    return arr;
  }, [topics, statusFilter, search]);

  function handleExportCsv() {
    const clientSlug = selected?.client_name?.toLowerCase().replace(/\s+/g, '-') || 'client';
    const date = new Date().toISOString().slice(0, 10);
    const columns = [
      { header: 'Priority', key: 'priority' },
      { header: 'Blog Topic', key: 'article_title' },
      { header: 'Status', key: 'status' },
      { header: 'Content Format', key: 'content_format' },
      { header: 'Buyers Journey', key: 'buyers_journey' },
      { header: 'Scheduled', key: 'scheduled_date' },
    ];
    const rows = filteredTopics.map((t) => ({
      ...t,
      status: topicDisplayMeta(t, { viewer }).label,
      scheduled_date: formatScheduledDate(t.scheduled_date) || '',
    }));
    exportCsv(columns, rows, `content-strategy-${clientSlug}-${date}.csv`);
  }

  if (!selectedCampaignId) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-domains-page">
      <header className="aivis-dash-header">
        <div>
          <h1 className="aivis-page-title">Content Strategy</h1>
          <p className="aivis-page-sub">
            {selected
              ? `Blog content delivery and tracking for "${selected.client_name}". Select a row to open details.`
              : 'Select a campaign to view content strategy.'}
          </p>
        </div>
      </header>

      {error ? <div className="aivis-auth-error">{error}</div> : null}
      {actionError ? <div className="aivis-auth-error">{actionError}</div> : null}
      {actionSuccess ? (
        <div style={{ color: 'var(--aivis-positive)', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'var(--aivis-positive-soft)', borderRadius: 8, border: '1px solid #bbf7d0' }}>
          {actionSuccess}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard
          label="Topics Planned"
          value={loading && !data ? '—' : (summary.topics_planned ?? 0)}
          loading={loading}
          subline={summary.new_this_quarter ? `+${summary.new_this_quarter} this quarter` : null}
          sublineColor="var(--aivis-positive)"
        />
        <StatCard
          label="Published"
          value={loading && !data ? '—' : `${summary.published ?? 0} / ${summary.topics_planned ?? 0}`}
          loading={loading}
          accent="var(--aivis-positive)"
        />
        <StatCard
          label="In Production"
          value={loading && !data ? '—' : (summary.in_production ?? 0)}
          loading={loading}
          accent="var(--aivis-info)"
          subline={summary.client_review ? `${summary.client_review} in ${viewer ? 'Approval Needed' : 'Ready for Review'}` : null}
          sublineColor="var(--aivis-caution-ink)"
        />
        <StatCard
          label="Completion"
          value={loading && !data ? '—' : `${summary.completion_pct ?? 0}%`}
          loading={loading}
          progress={summary.completion_pct ?? 0}
        />
      </div>

      <section className="aivis-dash-section" aria-label="Content topics table">
        <div className="aivis-dash-card">
          <div className="aivis-domains-table-toolbar" style={{ justifyContent: 'space-between' }}>
            <div className="aivis-segmented" role="tablist" aria-label="Filter by status">
              {(viewer ? CLIENT_STATUS_FILTERS : STATUS_FILTERS).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === opt.value}
                  className={`aivis-segmented-option${statusFilter === opt.value ? ' aivis-segmented-option--active' : ''}`}
                  onClick={() => setStatusFilter(opt.value)}
                >
                  {opt.label} {counts[opt.value] != null ? counts[opt.value] : ''}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="aivis-prompts-search-wrap" style={{ minWidth: 300, maxWidth: 720 }}>
                <SearchIcon />
                <input
                  type="search"
                  className="aivis-prompts-search"
                  placeholder="Search topics or keywords…"
                  value={inputValue}
                  onChange={handleSearchChange}
                  aria-label="Search topics"
                />
              </div>
              {filteredTopics.length > 0 && (
                <button type="button" className="aivis-csv-export-btn" onClick={handleExportCsv} title="Export visible rows as CSV">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export CSV
                </button>
              )}
            </div>
          </div>

          <div className="aivis-domains-table-scroll">
            <table className="aivis-domains-table">
              <colgroup>
                <col style={{ width: '5%' }} />
                <col style={{ width: viewer ? '24%' : '22%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: viewer ? '31%' : '33%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="aivis-domains-th-num" style={{ textAlign: 'center' }}>#</th>
                  <th scope="col">Blog Topic</th>
                  <th scope="col" style={{ textAlign: 'center' }}>Status</th>
                  <th scope="col" style={{ textAlign: 'center' }}>Content Format</th>
                  <th scope="col" style={{ textAlign: 'center' }}>Buyers Journey</th>
                  <th scope="col" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTopics.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="aivis-domains-empty">
                      {loading
                        ? 'Loading…'
                        : search || statusFilter !== 'all'
                          ? 'No topics match your filters.'
                          : selectedCampaignId
                            ? 'No content topics yet for this client.'
                            : 'Select a campaign.'}
                    </td>
                  </tr>
                ) : (
                  filteredTopics.map((t, idx) => {
                    const proposedUrl = t.proposed_url;
                    const display = topicDisplayMeta(t, { viewer });
                    return (
                      <tr key={t.id} className="aivis-domains-tr">
                        <td className="aivis-domains-td-num aivis-domains-td-rank" style={{ textAlign: 'center' }}>{t.priority ?? idx + 1}</td>
                        <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontWeight: 600, color: 'var(--aivis-text)' }}>{t.article_title}</span>
                              {proposedUrl ? (
                                <a
                                  href={proposedUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: 'var(--aivis-text-muted)', display: 'inline-flex' }}
                                  aria-label="Open proposed URL"
                                >
                                  <ExternalLinkIcon />
                                </a>
                              ) : null}
                            </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <StatusPill status={display.label} statusGroup={display.group} />
                        </td>
                        <td style={{ textAlign: 'center' }}>{t.content_format || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{t.buyers_journey || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          {viewer ? (
                            /* Client-side: never show Generate; hide View Doc while In Progress */
                            t.status === 'Post Sent to Client' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                                  <ViewDocButton url={t.google_doc_url} />
                                  <button
                                    type="button"
                                    style={PRIMARY_BTN_STYLE}
                                    onClick={() => setApproveTopic(t)}
                                  >
                                    <CheckIcon />
                                    Approve
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  style={REVISION_BTN_STYLE}
                                  onClick={() => setRequestChangesTopic(t)}
                                >
                                  Send Back with Comments
                                </button>
                              </div>
                            ) : t.status === 'Post Implemented' ? (
                              <ActionRow>
                                <ViewDocButton url={t.google_doc_url} />
                                <span style={{ fontSize: 13, color: 'var(--aivis-positive)', fontWeight: 600 }}>Implemented</span>
                              </ActionRow>
                            ) : display.group === 'in_progress' ? (
                              '—'
                            ) : (
                              hasGoogleDocUrl(t)
                                ? <ViewDocButton url={t.google_doc_url} />
                                : formatScheduledDate(t.scheduled_date)
                                  ? <ScheduledLabel scheduledDate={t.scheduled_date} />
                                  : '—'
                            )
                          ) : (
                            <CompanyTopicActions
                              topic={t}
                              onGenerate={setGenerateTopic}
                              onApprove={setApproveTopic}
                              onProcessRevision={setProcessRevisionTopic}
                              onImplement={handleImplement}
                              implementTopicId={implementTopicId}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filteredTopics.length > 0 ? (
            <p className="aivis-domains-count">
              {filteredTopics.length.toLocaleString()} topic{filteredTopics.length === 1 ? '' : 's'}
            </p>
          ) : null}
        </div>
      </section>

      {generateTopic && !viewer ? (
        <GenerateModal
          topic={generateTopic}
          campaignId={selectedCampaignId}
          onClose={() => setGenerateTopic(null)}
          onSuccess={() => {
            setGenerateTopic(null);
            setActionSuccess(`Generation fired for "${generateTopic.article_title}". The Google Doc URL will appear once processing completes — refresh later to see it.`);
            setTimeout(() => setActionSuccess(''), 6000);
            fetchData();
          }}
        />
      ) : null}

      {approveTopic ? (
        <ApproveModal
          topic={approveTopic}
          campaignId={selectedCampaignId}
          onClose={() => setApproveTopic(null)}
          onSuccess={() => {
            setApproveTopic(null);
            setActionSuccess(
              approveTopic.status === 'Post Sent to Client'
                ? "Approved. We'll let you know when it's live."
                : `Approval sent for "${approveTopic.article_title}".`
            );
            setTimeout(() => setActionSuccess(''), 4000);
            fetchData();
          }}
        />
      ) : null}

      {processRevisionTopic && !viewer ? (
        <ProcessRevisionModal
          topic={processRevisionTopic}
          campaignId={selectedCampaignId}
          onClose={() => setProcessRevisionTopic(null)}
          onSuccess={() => {
            setProcessRevisionTopic(null);
            setActionSuccess(
              `"${processRevisionTopic.article_title}" revision processed. It will be sent to the client for final approval, or auto-approved, according to their preference.`
            );
            setTimeout(() => setActionSuccess(''), 6000);
            fetchData();
          }}
        />
      ) : null}

      {requestChangesTopic ? (
        <RequestChangesModal
          topic={requestChangesTopic}
          campaignId={selectedCampaignId}
          onClose={() => setRequestChangesTopic(null)}
          onSuccess={() => {
            setRequestChangesTopic(null);
            setActionSuccess(
              `${requestChangesTopic.article_title} has been sent back to our team. We'll start working on the revisions ASAP and follow up once it's revised.`
            );
            setTimeout(() => setActionSuccess(''), 7000);
            fetchData();
          }}
        />
      ) : null}
    </div>
  );
}
